<template>
  <Card :class="$style.container">
    <div :class="$style.header">
      <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" :class="$style.reportIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.683 14h8.4a2.1 2.1 0 002.1-2.1V4.466a2.1 2.1 0 00-.528-1.393L11.501 1.77 10.604.729A2.1 2.1 0 009.013 0h-6.33a2.1 2.1 0 00-2.1 2.1v9.8c0 1.16.94 2.1 2.1 2.1zm7-12.194l.764.886.716.808h-1.48V1.806zm-1.4-.406v2.8a.7.7 0 00.7.7h2.8v7a.7.7 0 01-.7.7h-8.4a.7.7 0 01-.7-.7V2.1a.7.7 0 01.7-.7h5.6zM4.083 7a.7.7 0 01.7-.7h4.2a.7.7 0 010 1.4h-4.2a.7.7 0 01-.7-.7zm.7 2.1a.7.7 0 100 1.4h4.2a.7.7 0 000-1.4h-4.2z" fill="#4373C7"/></svg>
      <h4 :class="$style.title">
        {{ reportName }}
      </h4>
      <div
        v-if="missingResource"
        :class="$style.emptyStateLink"
      >
        <a
          :href="missingResourceRoute"
        >{{ missingResourceLinkName }}</a>
        to start generating {{ missingResourceReportName }}
      </div>
      <ReportExportActionButton
        :disabled="!!missingResource"
        :filters="filters"
        :isLoading="checkingForMissingResource"
        :reportType="reportType"
      />
      <IconTooltip>
        <p>
          JumpCloud reports are available for export in JSON via the API. See the
          <a
            :href="apiLink"
            rel="noopener noreferrer"
            target="_blank"
          >
            API Docs
          </a>
          for steps to generate in JSON.
        </p>
      </IconTooltip>
    </div>
    <hr :class="$style.divider">
    <div :class="$style.content">
      <div :class="$style.info">
        <br>
        <p>Here's what's included in each report:</p>
      </div>
      <div>
        <img
          :class="$style.previewImage"
          :src="previewImage"
        >
      </div>
    </div>
  </Card>
</template>

<script>
import { ItemTypeConsts, ItemTypeDisplayNames, ItemTypeRoutes } from 'client/util/Constants';
import { S3BucketAddress } from 'client/util/Constants/ApplicationConstants';
import ApplicationApi from 'client/api/v2/ApplicationApi';
import Card from 'client/components/Card.vue';
import CommandResultsApi from 'client/api/v2/CommandResultsApi';
import DirectoryApi from 'client/api/v2/DirectoryApi';
import IconTooltip from 'client/components/Tooltip/IconTooltip';
import PolicyApi from 'client/api/v2/PolicyApi';
import RadiusServerApi from 'client/api/v2/RadiusServerApi';
import ReportExportActionButton from 'client/Reports/components/ReportExportActionButton.vue';
import ReportMetadata from 'client/Reports/ReportMetadata';
import SystemApi from 'client/api/v2/SystemApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import pluralize from 'pluralize';

export default {
  name: 'ReportExportContainer',

  components: {
    Card,
    IconTooltip,
    ReportExportActionButton,
  },

  props: {
    filters: {
      type: Array,
      default() {
        return [];
      },
    },
    reportType: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    apiLink: 'https://docs.jumpcloud.com/api/insights/directory/1.0/index.html#tag/Reports',
    checkingForMissingResource: true,
    missingResource: null,
    usersRoute: ItemTypeRoutes[ItemTypeConsts.systemUser],
  }),

  computed: {
    missingResourceLinkName() {
      const resourceLinkMap = {
        [ItemTypeConsts.ldap]: 'Add LDAP',
        [ItemTypeConsts.commandResult]: 'Execute a command',
      };

      const defaultName = `Add ${pluralize(ItemTypeDisplayNames[this.missingResource])}`;

      return resourceLinkMap[this.missingResource] || defaultName;
    },

    missingResourceReportName() {
      const { reportName } = this;
      const resourceReportName = {
        [ItemTypeConsts.systemUser]: 'User to Resource Report',
        [ItemTypeConsts.commandResult]: `To generate a ${reportName}`,
      };

      const defaultName = `${reportName}s`;

      return resourceReportName[this.missingResource] || defaultName;
    },

    missingResourceRoute() {
      return ItemTypeRoutes[this.missingResource];
    },

    previewImage() {
      return `${S3BucketAddress}${ReportMetadata[this.reportType].previewImage}`;
    },

    reportName() {
      return `${ReportMetadata[this.reportType].name} Report`;
    },
  },

  watch: {
    reportType() {
      /**
       * TODO: Instead of checking every time, we should store whether we have checked for these
       * resources already, possibly through a Vuex store module.
       */
      this.checkForResources();
    },
  },

  created() {
    this.checkForResources();
  },

  methods: {
    async checkForResources() {
      const { resourcesNeeded } = ReportMetadata[this.reportType];

      this.missingResource = null;
      for (let i = 0; i < resourcesNeeded.length; i += 1) {
        const resource = resourcesNeeded[i];

        // @see https://eslint.org/docs/rules/no-await-in-loop#when-not-to-use-it
        // eslint-disable-next-line no-await-in-loop
        const hasResource = await this.checkHasResource(resource);
        if (!hasResource) {
          this.missingResource = resource;
          break;
        }
      }
      this.checkingForMissingResource = false;
    },

    async checkHasResource(resource) {
      if (resource === ItemTypeConsts.ssoApplications) {
        const { totalCount } = await ApplicationApi.fetch(0, 1);
        return totalCount > 0;
      }
      if (resource === ItemTypeConsts.commandResult) {
        const { totalCount } = await CommandResultsApi.fetch(0, 1);
        return totalCount > 0;
      }
      if (resource === ItemTypeConsts.device) {
        const { totalCount } = await SystemApi.fetch(0, 1);
        return totalCount > 0;
      }
      if (resource === ItemTypeConsts.directory) {
        const filters = [
          { name: 'activated', operator: 'eq', value: true },
          { name: 'type', operator: 'ne', value: 'ldap_server' },
        ];
        const { totalCount } = await DirectoryApi.fetch(0, 1, null, null, null, filters);
        return totalCount > 0;
      }
      if (resource === ItemTypeConsts.ldap) {
        const filters = [
          { name: 'activated', operator: 'eq', value: true },
          { name: 'type', operator: 'eq', value: 'ldap_server' },
        ];
        const { totalCount } = await DirectoryApi.fetch(0, 1, null, null, null, filters);
        return totalCount > 0;
      }
      if (resource === ItemTypeConsts.browserPatchManagement) {
        const { totalCount } = await PolicyApi.fetchTotalPolicyCountByName(['chrome_universal', 'firefox_universal', 'edge_universal']);
        return totalCount > 0;
      }
      if (resource === ItemTypeConsts.osPatchManagement) {
        const { totalCount } = await PolicyApi.fetchTotalPolicyCount();
        return totalCount > 0;
      }
      if (resource === ItemTypeConsts.radiusServer) {
        const { totalCount } = await RadiusServerApi.fetch(0, 1);
        return totalCount > 0;
      }
      if (resource === ItemTypeConsts.systemUser) {
        const { totalCount } = await SystemUserApi.fetch(0, 1);
        return totalCount > 0;
      }
      if (resource === ItemTypeConsts.userGroup) {
        const { totalCount } = await UserGroupApi.fetchTotalUserGroups();
        return totalCount > 0;
      }
      return false;
    },
  },
};

</script>

<style module>
@value (jcGrey200) from '~css/brand-colors.css';
@value (white) from '~css/colors.css';

.container {
  display: grid;
  grid-template-columns: 1fr auto;
  margin: 10px 0 20px;
}

.header {
  align-items: center;
  display: flex;
  padding: 0 15px 16px;
}

.content,
.divider.divider {
  grid-column: 1 / span 2;
}

.divider.divider {
  color: jcGrey200;
  height: 1px;
  margin-top: 0;
  width: 100%;
}

.title.title {
  flex: 1;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 2rem;
  margin: 0;
}

.reportIcon {
  margin-right: 10px;
  vertical-align: middle;
  width: 26px;
}

.info {
  font-size: 1.2rem;
  padding-left: 15px;
}

.previewImage {
  display: block;
  margin: 32px auto 0;
  max-width: 1100px;
  pointer-events: none;
  user-select: none;
}

.emptyStateLink {
  padding: 0 20px;
}
</style>
